import { motion } from "framer-motion";
import {
  CubeTransparentIcon,
  ShieldCheckIcon,
  BoltIcon,
  GlobeAltIcon,
  BellIcon,
  ChartBarIcon,
  CpuChipIcon,
  AcademicCapIcon,
} from "@heroicons/react/24/outline";
import { useSettings } from "../contexts/SettingsContext";
import { cn } from "../utils/cn";
import { EXTERNAL_LINKS } from "../constants/links";

const iconMap = {
  "Drag & Drop Builder": CubeTransparentIcon,
  "EIP-7702 Smart Wallets": ShieldCheckIcon,
  "Real-time Price Feeds": BoltIcon,
  "Multi-chain Support": GlobeAltIcon,
  "Advanced Analytics": ChartBarIcon,
  "DeFAI agents": CpuChipIcon,
  "Quant Strategies": AcademicCapIcon,
  "Smart Notifications": BellIcon,
};

export default function Features() {
  const { settings } = useSettings();
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  return (
    <section id="features" className="py-24 relative overflow-hidden">
      {/* Top fade from previous section */}
      <div className="pointer-events-none absolute inset-x-0 -top-16 h-16 bg-gradient-to-b from-transparent to-white/80 dark:to-[#0b0f19]/80 [mask-image:linear-gradient(to_bottom,black,transparent)]" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <header className="text-center mb-16">
          <motion.h2
            variants={itemVariants}
            className="text-3xl sm:text-4xl lg:text-5xl font-oxanium font-bold mb-6"
            style={{ color: settings.primaryColor }}
          >
            {settings.featuresTitle}
          </motion.h2>
          <motion.p
            variants={itemVariants}
            className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto"
          >
            {settings.featuresSubtitle}
          </motion.p>
        </header>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {settings.featuresCards.map((feature) => {
            const IconComponent =
              iconMap[feature.name as keyof typeof iconMap] ||
              CubeTransparentIcon;
            return (
              <motion.div
                key={feature.name}
                variants={itemVariants}
                whileHover={{
                  scale: 1.05,
                  rotateY: 5,
                }}
                className="group relative"
              >
                <div
                  className={cn(
                    "h-full p-6 rounded-2xl glass-effect",
                    "hover:bg-white/20 dark:hover:bg-white/10",
                    "transition-all duration-300",
                    "border border-white/20 dark:border-white/10",
                    "hover:border-white/30 dark:hover:border-white/20"
                  )}
                >
                  {/* Icon with solid background */}
                  <motion.div
                    className="w-12 h-12 rounded-xl mb-4 flex items-center justify-center shadow-lg"
                    style={{ backgroundColor: "#e95c49" }}
                    whileHover={{ rotate: 5, scale: 1.1 }}
                    transition={{ type: "spring", stiffness: 300, damping: 20 }}
                  >
                    <IconComponent className="h-6 w-6 text-white" />
                  </motion.div>

                  {/* Content */}
                  <h3 className="text-xl font-semibold mb-3 text-gray-900 dark:text-white font-oxanium">
                    {feature.name}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed">
                    {feature.description}
                  </p>

                  {/* Hover effect overlay */}
                  <motion.div
                    className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-10 transition-opacity duration-300"
                    style={{ backgroundColor: "#e95c49" }}
                  />

                  {/* Glow effect */}
                  <motion.div
                    className="absolute -inset-0.5 rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300 blur-sm"
                    style={{ backgroundColor: "#e95c49" }}
                  />
                </div>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
          className="text-center mt-16"
        >
          <motion.button
            onClick={() => {
              // Open waitlist modal or navigate to a features page
              const waitlistModal = document.querySelector(
                "[data-waitlist-modal]"
              ) as HTMLElement;
              if (waitlistModal) {
                waitlistModal.click();
              } else {
                // Navigate to documentation
                // window.location.href = EXTERNAL_LINKS.keyFeatures;
                //open in a new window
                window.open(EXTERNAL_LINKS.keyFeatures, "_blank");
              }
            }}
            className="px-8 py-4 rounded-xl font-semibold text-lg text-white shadow-lg hover:shadow-xl transition-all duration-300"
            style={{
              backgroundColor: "#e95c49",
              boxShadow: "0 10px 25px rgba(233, 92, 73, 0.25)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#e95c49";
              e.currentTarget.style.boxShadow =
                "0 20px 40px rgba(233, 92, 73, 0.4)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "#e95c49";
              e.currentTarget.style.boxShadow =
                "0 10px 25px rgba(233, 92, 73, 0.25)";
            }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
          >
            {settings.featuresButtonText}
          </motion.button>
        </motion.div>
      </div>

      {/* Bottom fade to next section */}
      <div className="pointer-events-none absolute inset-x-0 -bottom-16 h-16 bg-gradient-to-t from-transparent to-white/80 dark:to-[#0b0f19]/80 [mask-image:linear-gradient(to_top,black,transparent)]" />

      {/* Floating elements */}
      <motion.div
        className="absolute top-20 right-10 w-20 h-20 rounded-full blur-xl"
        style={{ backgroundColor: "rgba(233, 92, 73, 0.2)" }}
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.3, 0.6, 0.3],
        }}
        transition={{
          duration: 4,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />

      <motion.div
        className="absolute bottom-20 left-10 w-32 h-32 rounded-full blur-xl"
        style={{ backgroundColor: "rgba(233, 92, 73, 0.15)" }}
        animate={{
          scale: [1.2, 1, 1.2],
          opacity: [0.2, 0.5, 0.2],
        }}
        transition={{
          duration: 6,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />
    </section>
  );
}
